import { UI, Skeleton, SkeletonTypes, HStack, Stack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React from 'react';

export interface PerformanceSummaryMetricProps {
  label: string;
  value: number;
  hideValue?: boolean;
  performanceComparisonLabel: string;
  performanceComparisonValue: number;
  performanceComparisonPercentage: number;
  isMobile?: boolean;
  withError?: boolean;
  isLoading?: boolean;
}

export const ColoredSkeleton = (props: SkeletonTypes.Props) => (
  <Skeleton {...props} endColor="brand.primary.lightest" borderRadius="full" />
);

export const PerformanceSummaryMetric: React.FC<PerformanceSummaryMetricProps> = ({
  label,
  performanceComparisonLabel,
  performanceComparisonPercentage,
  performanceComparisonValue,
  value,
  hideValue,
  isMobile,
  isLoading,
  withError,
}) => {
  const valueIsPositive = value > 0;

  const renderValue = () => {
    if (isLoading) {
      return <ColoredSkeleton width="80px" height={Typography['fontsize.lg']} />;
    }

    if (hideValue) {
      return (
        <UI.Text
          fontSize={Typography['fontsize.xxs']}
          variant="semibold"
          color="neutral.darkest"
          letterSpacing={Typography['bullet.spacing']}>
          {Typography['bullet.sequence']}
        </UI.Text>
      );
    }

    const valueToRender = withError ? '--' : Utils.Mask.Scale(value);

    return (
      <UI.Text
        color={valueIsPositive ? 'neutral.black' : 'neutral.medium'}
        variant="semibold"
        {...(!isMobile && { fontSize: Typography['fontsize.lg'] })}>
        {valueToRender}
      </UI.Text>
    );
  };

  const renderComparison = () => {
    if (isLoading) {
      return <ColoredSkeleton width="full" height={Typography['fontsize.xs']} />;
    }

    const valueToRender = withError ? '--' : Utils.Mask.Scale(performanceComparisonValue);

    return (
      <UI.Text fontSize={Typography['fontsize.xs']} color="neutral.medium">
        {`${performanceComparisonLabel}: ${valueToRender}`}
      </UI.Text>
    );
  };

  return (
    <VStack space={Spacing['stack.quarck']}>
      <Stack
        space={Spacing['stack.quarck']}
        alignItems="center"
        {...(isMobile
          ? { direction: 'row', justifyContent: 'space-between' }
          : { direction: 'column' })}>
        <UI.Text color="neutral.medium" variant="semibold">
          {label}
        </UI.Text>
        {renderValue()}
      </Stack>
      <HStack justifyContent={isMobile ? 'space-between' : 'inherit'} space="inline.quarck">
        {renderComparison()}
        {!withError && !isLoading && (
          <UI.Text fontSize="xs" color="neutral.dark">
            ({Utils.Mask.Percentage(performanceComparisonPercentage)})
          </UI.Text>
        )}
      </HStack>
    </VStack>
  );
};
